import { useToolPreferencesStore } from "@/stores/ToolPreferencesStore";
import { getBaseAgentTools } from "@/stores/WorkflowStore";
import { logger } from "@/telemetry";

/**
 * Tools chat switches on by default whether or not the workflow definition
 * lists them, so they are recorded as though the definition carried them.
 *
 * Without this, switching one off against a definition that omits it produces a
 * selection identical to the definition, the delta prunes itself to nothing,
 * and the initialisation effect that applies the default switches it straight
 * back on at the next page load.
 */
const DEFAULT_ON_TOOLS = ["save_to_memory"];

/**
 * Record one chat tool toggle as a difference from the loaded definition.
 *
 * The definition's own tool list comes from the snapshot `loadWorkflow` takes
 * before it applies the overlay, so the caller never has to work out what the
 * shared workflow carried. Callers pass the tool they toggled rather than the
 * resulting list, because reading the list back catches whatever else has
 * written to the store in the same commit: the composer's web search effect
 * fires alongside the memory effect, and diffing the list there recorded
 * `serpapi_search` and `crawler` as personal preferences (LSEG-190).
 *
 * A null snapshot means no overlay is in play, which is how the Workflow
 * Builder and the Playground load a workflow. Nothing is recorded in that case,
 * or a personal preference would attach itself to an editor's view of the
 * shared definition. The snapshot is also refused when it belongs to a
 * different workflow, which is what a switch between render and click leaves
 * behind: recording against the wrong baseline would invert `enabled` and
 * `disabled` for every tool the two definitions do not share.
 */
export function recordToolToggle(
  workflowId: string,
  agentId: string,
  toolId: string,
  isSelected: boolean,
): void {
  const snapshot = getBaseAgentTools(workflowId);

  // No snapshot means no overlay is in play, which is the normal state for the
  // editor surfaces; nothing should be recorded there.
  if (!snapshot) return;

  const baseTools = snapshot[agentId];

  if (!baseTools) {
    logger.warn("Tool toggle not recorded: agent is missing from the definition snapshot", {
      workflowId,
      agentId,
    });

    return;
  }

  const isInDefinition = baseTools.includes(toolId) || DEFAULT_ON_TOOLS.includes(toolId);

  useToolPreferencesStore
    .getState()
    .setAgentToolPreference(workflowId, agentId, toolId, isSelected, isInDefinition);
}

/**
 * Whether the user has recorded a decision about one specific tool.
 *
 * Asked per tool rather than per workflow, so that a preference recorded for
 * some other connector cannot suppress a feature default the user has never
 * expressed a view on.
 */
export function hasRecordedToolPreference(
  workflowId: string,
  agentId: string,
  toolId: string,
): boolean {
  const preference = useToolPreferencesStore.getState().getAgentPreference(workflowId, agentId);

  if (!preference) return false;

  return preference.enabled.includes(toolId) || preference.disabled.includes(toolId);
}
